import * as React from "react";
import {
  Backdrop,
  Button,
  CircularProgress,
  Grid,
  InputLabel,
  Modal as MuiModal,
  TextField,
  InputAdornment,
  Typography,
} from "@mui/material";
import { ClearIcon } from "@mui/x-date-pickers/icons";
import { AuthenticationContext } from "../Context";
import { getHeaders } from "../utils/HeaderHelper";
import { formatMonetaryValue, formatMonetaryInput } from "../utils/MonetaryFormatHelper";
import { useFlash } from "../FlashContext";

interface Props {
  showAddCptCodeChargeModal: boolean;
  closeAddCptCodeChargeModal: () => void;
  selectedCptCodeCharge: any;
  setRefreshCptCodeCharges: any;
}

const MAX_CPT_CODE_LENGTH = 10;
const CPT_DESCRIPTION_MAX_LENGTH = 200;
const CHARGE_AMOUNT_MAX_DIGITS = 10;

const AddCptCodeCharge: React.FC<Props> = ({
  showAddCptCodeChargeModal,
  closeAddCptCodeChargeModal,
  selectedCptCodeCharge,
  setRefreshCptCodeCharges,
}) => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();

  const [cptCode, setCptCode] = React.useState(
    selectedCptCodeCharge?.cpt_code ?? ""
  );
  const [cptCodeDescription, setCptCodeDescription] = React.useState(
    selectedCptCodeCharge?.cpt_code_description ?? ""
  );
  const [chargeAmount, setChargeAmount] = React.useState(
    selectedCptCodeCharge?.charge_amount ?? ""
  );
  const [loading, setLoading] = React.useState(false);

  const handleSubmit = async () => {
    if (!cptCode) {
      showToast({
        title: "Failed to Save CPT Code Charge",
        text: "Please enter CPT Code",
        type: "error",
      });
    } else if (!chargeAmount) {
      showToast({
        title: "Failed to Save CPT Code Charge",
        text: "Please enter Charge Amount",
        type: "error",
      });
    } else {
      setLoading(true);

      const normalizeForSave = (value?: string): string => {
        return value
          ?.trim()
          .replace(/\s+/g, " ")
          ?? "";
      };
      
      const cpt_code_charge = {
        cpt_code: normalizeForSave(cptCode),
        cpt_code_description: normalizeForSave(cptCodeDescription),
        charge_amount: formatMonetaryValue(chargeAmount),
      };

      const url = selectedCptCodeCharge
        ? `/cpt_code_charges/${selectedCptCodeCharge.id}`
        : "/cpt_code_charges";

      const method = selectedCptCodeCharge ? "PUT" : "POST";

      try {
        const response = await fetch(url, {
          method,
          headers: getHeaders(authenticationSetting.csrfToken),
          body: JSON.stringify({ cpt_code_charge }),
        });
        const result = await response.json();
        if (result.success) {
          showToast({
            title: "CPT Code Charge Saved Successfully",
            text: `CPT Code Charge ${
              selectedCptCodeCharge ? "updated" : "created"
            } successfully`,
            type: "success",
          });
          setRefreshCptCodeCharges((prev) => !prev);
          closeAddCptCodeChargeModal();
        } else {
          showToast({
            title: "Failed to Save CPT Code Charge",
            text: result.message || "Failed to save CPT Code Charge",
            type: "error",
          });
        }
      } catch (error) {
        showToast({
          title: "Failed to Save CPT Code Charge",
          text: error.message ?? "Failed to save CPT Code Charge",
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <MuiModal
      open={showAddCptCodeChargeModal}
      className="modal-primary"
      onClose={closeAddCptCodeChargeModal}
    >
      <div className="paper" style={{ width: 430 }}>
        <div className="paperInner">
          <Grid container>
            <Grid item xs={12}>
              <div className="main-header">
                <span>CPT Code Charge</span>
                <span id="dismiss-button" onClick={closeAddCptCodeChargeModal}>
                  <ClearIcon sx={{ width: 30, height: 30 }} />
                </span>
              </div>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                paddingLeft: "24px",
                paddingRight: "24px",
                display: "flex",
                flexDirection: "column",
                rowGap: "20px",
              }}
            >
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="cpt_code" className="field-label">
                    CPT Code*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="cpt_code"
                    size="small"
                    value={cptCode}
                    className="the-field"
                    required
                    variant="outlined"
                    placeholder="XXXXXXXXXX"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const rawValue = event.target.value;
                      const sanitizedValue = rawValue.replace(/[^a-zA-Z0-9]/g, "");
                      setCptCode(sanitizedValue);
                    }}
                    sx={{
                      width: "100% !important",
                      backgroundColor: "#efe9e7 !important",
                      borderRadius: "5px",
                      "& fieldset": {
                        border: "none",
                        outline: "none",
                      },
                    }}
                    slotProps={{
                      htmlInput: {
                        maxLength: MAX_CPT_CODE_LENGTH,
                      },
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel
                    htmlFor="cpt_code_description"
                    className="field-label"
                  >
                    CPT Code Description
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="cpt_code_description"
                    size="small"
                    value={cptCodeDescription}
                    className="the-field"
                    required
                    variant="outlined"
                    placeholder="Enter Description"
                    onChange={(event) => {
                      setCptCodeDescription(event.target.value);
                    }}
                    inputProps={{ maxLength: CPT_DESCRIPTION_MAX_LENGTH }}
                    sx={{
                      width: "100% !important",
                      backgroundColor: "#efe9e7 !important",
                      borderRadius: "5px",
                      "& fieldset": {
                        border: "none",
                        outline: "none",
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="charge_amount" className="field-label">
                    Charge Amount*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="charge_amount"
                    size="small"
                    value={chargeAmount}
                    variant="outlined"
                    className="the-field"
                    placeholder="####.##"
                    required
                    autoComplete="off"
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      const digitsOnlyCount = rawValue.replace(/\D/g, "").length;
                      if (digitsOnlyCount > CHARGE_AMOUNT_MAX_DIGITS) return;
                      const formatted = formatMonetaryInput(rawValue);
                      if (formatted !== null) setChargeAmount(formatted);
                    }}
                    onBlur={(e) => {
                      const formatted = formatMonetaryValue(e.target.value);
                      if (formatted !== "") setChargeAmount(formatted);
                    }}
                    sx={{
                      width: "100% !important",
                      backgroundColor: "#efe9e7 !important",
                      borderRadius: "5px",
                      "& .MuiInputBase-input": {
                        paddingLeft: chargeAmount ? "0px !important" : undefined,
                      },
                      "& .MuiOutlinedInput-root": {
                        paddingLeft: 0,
                      },
                    }}
                    slotProps={{
                      input: {
                        inputMode: "numeric",
                        startAdornment: chargeAmount && (
                          <InputAdornment
                            position="start"
                            sx={{
                              marginRight: "0px",
                              paddingLeft: "10.5px",
                            }}
                          >
                            <Typography sx={{ fontSize: "14px" }}>$</Typography>
                          </InputAdornment>
                        ),
                      },
                      htmlInput: {
                        maxLength: CHARGE_AMOUNT_MAX_DIGITS + 1,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            className="modal-footer"
          >
            <Grid item xs={12} className="confirm-btn-container">
              <div onClick={closeAddCptCodeChargeModal} className="cancel-link">
                Cancel
              </div>
              <Button
                onClick={handleSubmit}
                className="confirm-btn"
                style={{ width: 156 }}
              >
                {selectedCptCodeCharge ? "Update" : "Create"}
              </Button>
            </Grid>
          </Grid>
        </div>
        <Backdrop open={loading} style={{ zIndex: 9999, color: "#fff" }}>
          <CircularProgress />
        </Backdrop>
      </div>
    </MuiModal>
  );
};

export default AddCptCodeCharge;
